import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
// Start: CAP-39922 DMBR-64
import { TranslateService } from "@ngx-translate/core";
// End: CAP-39922 DMBR-64
import { ScreenSizeService } from "src/app/services/screen-size.service";
import { VinSearchService } from "src/app/services/vin-search.service";
import { CacheService } from "src/app/services/cache.service";
import { DIMBO_CONSTANT } from "src/app/utils/constant";
import {
  SubmissionHistory,
  SubmissionHistoryFilterResponse,
} from "src/app/model/submission-history";
// Start: CAP-39922 DMBR-64
import {
  DateAdapter,
  NativeDateAdapter,
  MAT_DATE_FORMATS,
} from "@angular/material/core";
import { Injectable } from "@angular/core";

/** Custom date adapter that formats dates as dd/MM/yyyy. */
@Injectable()
export class DdMmYyyyDateAdapter extends NativeDateAdapter {
  format(date: Date, displayFormat: Object): string {
    if (displayFormat === "dd/MM/yyyy") {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return super.format(date, displayFormat);
  }

  parse(value: any): Date | null {
    if (typeof value === "string") {
      const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (match) {
        const day = +match[1];
        const month = +match[2] - 1;
        const year = +match[3];
        const date = new Date(year, month, day);
        // Verify the date components match (catches invalid days like 32/13/2026)
        if (
          date.getFullYear() === year &&
          date.getMonth() === month &&
          date.getDate() === day
        ) {
          return date;
        }
      }
      return this.invalid();
    }
    return super.parse(value);
  }
}

const DD_MM_YYYY_FORMATS = {
  parse: { dateInput: "dd/MM/yyyy" },
  display: {
    dateInput: "dd/MM/yyyy",
    monthYearLabel: { year: "numeric", month: "short" },
    dateA11yLabel: "dd/MM/yyyy",
    monthYearA11yLabel: { year: "numeric", month: "long" },
  },
};
// End: CAP-39922 DMBR-64

@Component({
  selector: "app-submission-history",
  templateUrl: "./submission-history.component.html",
  styleUrls: ["./submission-history.component.scss"],
  // Start: CAP-39922 DMBR-64
  providers: [
    { provide: DateAdapter, useClass: DdMmYyyyDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_FORMATS },
  ],
  // End: CAP-39922 DMBR-64
})
export class SubmissionHistoryComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild("scrollAnchor") scrollAnchor?: ElementRef<HTMLDivElement>;

  @Input() submissionMode: boolean = false;
  @Input() defaultVin: string = "";
  @Input() isCentralUser: boolean = false;

  /** Emits the VIN whenever a VIN search is triggered (auto-fill or user-entered). */
  @Output() vinSearchPerformed = new EventEmitter<string>();

  /** Emits VIN + results after the submission-history API call completes. */
  @Output() vinSearchCompleted = new EventEmitter<{
    vin: string;
    results: SubmissionHistory[];
    hasError: boolean;
  }>();

  searchForm!: FormGroup;
  isMobile: boolean = false;
  isTablet: boolean = false;
  isPanelExpanded: boolean = true;
  maxDate: Date = new Date();

  // What's actually rendered on screen
  displayedResults: SubmissionHistory[] = [];
  loading: boolean = false;
  loadingMore: boolean = false;
  errorMessage: string = "";
  hasSearched: boolean = false;

  /** True when the last search was a VIN search (show current contract badge). */
  isVinSearch: boolean = false;

  private allFetched: SubmissionHistory[] = [];
  private displayIndex: number = 0;
  // Start: CAP-39922 DMBR-64
  private readonly PAGE_SIZE = 10;
  // End: CAP-39922 DMBR-64
  private fetchPage: number = 0;
  private hasMoreOnServer: boolean = false;
  showBackToTop: boolean = false;

  // Start: CAP-39922 DMBR-64
  totalResultCount: number = 0;
  filterStartDate: string = "";
  filterEndDate: string = "";
  // End: CAP-39922 DMBR-64

  /** Cached RRDI for AR users. */
  private arRrdi: string = "";
  /** Cached Physical Site for AR users. */
  private arPhysicalSite: string = "";

  private observer?: IntersectionObserver;

  constructor(
    private screenSizeService: ScreenSizeService,
    private fb: FormBuilder,
    private toastrService: ToastrService,
    private vinSearchService: VinSearchService,
    private cacheService: CacheService,
    // Start: CAP-39922 DMBR-64
    private translateService: TranslateService,
    // End: CAP-39922 DMBR-64
  ) {}

  ngOnInit(): void {
    this.screenSizeService.screenWidth$.subscribe((width) => {
      this.isMobile = width <= 480;
      this.isTablet = width > 480 && width <= 768;
    });
    window.addEventListener("scroll", () => {
      this.showBackToTop = window.scrollY > 300;
    });
    this.initForm();
    this.initArDefaults();

    // If a default VIN is provided (from JWT payload), pre-fill and auto-search
    if (this.defaultVin) {
      this.searchForm.patchValue({ vehicleVin: this.defaultVin });
      this.onVinSearch();
    }
  }

  ngAfterViewInit(): void {
    this.setupObserver();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  initForm() {
    const today = new Date();
    this.searchForm = this.fb.group({
      startDate: [today],
      endDate: [today],
      repairerId: [""],
      searchScope: [false],
      vehicleVin: [
        "",
        [
          Validators.minLength(17),
          Validators.maxLength(17),
          Validators.pattern(/^(?=.*[A-Z])(?=.*[0-9])[A-Z0-9]*$/),
        ],
      ],
    });
  }

  togglePanel() {
    this.isPanelExpanded = !this.isPanelExpanded;
  }

  /**
   * For AR users: read RRDI and Physical Site from cache, pre-fill the
   * repairerId field with the value matching the current search scope,
   * and listen for scope toggles so the value swaps automatically.
   */
  private initArDefaults(): void {
    if (this.isCentralUser) {
      return;
    }
    this.arRrdi =
      this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_RRDI) || "";
    this.arPhysicalSite =
      this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_PHSYSICAL_SITE) || "";

    // Set the initial value based on the current scope (default = RRDI)
    const isPhysicalSite = this.searchForm.get("searchScope")?.value;
    this.searchForm
      .get("repairerId")
      ?.setValue(isPhysicalSite ? this.arPhysicalSite : this.arRrdi);

    // Swap value whenever the toggle changes
    this.searchForm
      .get("searchScope")
      ?.valueChanges.subscribe((val: boolean) => {
        this.searchForm
          .get("repairerId")
          ?.setValue(val ? this.arPhysicalSite : this.arRrdi);
      });
  }

  private resetResults(): void {
    this.displayedResults = [];
    this.allFetched = [];
    this.displayIndex = 0;
    this.fetchPage = 0;
    this.hasMoreOnServer = false;
    this.errorMessage = "";
    // Start: CAP-39922 DMBR-64
    this.totalResultCount = 0;
    this.filterStartDate = "";
    this.filterEndDate = "";
    // End: CAP-39922 DMBR-64
  }

  onFilterSearch(): void {
    const startVal: Date | null = this.searchForm.get("startDate")?.value;
    const endVal: Date | null = this.searchForm.get("endDate")?.value;

    // Start: CAP-39922 DMBR-64
    // Validate date format for manually entered dates
    if (!(startVal instanceof Date) || isNaN(startVal.getTime())) {
      this.toastrService.error(
        this.translateService.instant(
          "dimbo.i18n.submissionHistory.error.invalidDateFormat",
        ),
      );
      return;
    }
    if (!(endVal instanceof Date) || isNaN(endVal.getTime())) {
      this.toastrService.error(
        this.translateService.instant(
          "dimbo.i18n.submissionHistory.error.invalidDateFormat",
        ),
      );
      return;
    }
    // End: CAP-39922 DMBR-64

    // Start: CAP-39922 DMBR-64 — Normalize times: start at 00:00:00, end at 23:59:59
    const normalizedStart = new Date(startVal.getFullYear(), startVal.getMonth(), startVal.getDate(), 0, 0, 0, 0);
    const normalizedEnd = new Date(endVal.getFullYear(), endVal.getMonth(), endVal.getDate(), 23, 59, 59, 999);

    // Reject future dates (date-only comparison)
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const startDateOnly = new Date(normalizedStart.getFullYear(), normalizedStart.getMonth(), normalizedStart.getDate());
    const endDateOnly = new Date(normalizedEnd.getFullYear(), normalizedEnd.getMonth(), normalizedEnd.getDate());
    if (startDateOnly.getTime() > todayDate.getTime()) {
      this.toastrService.error(
        this.translateService.instant(
          "dimbo.i18n.submissionHistory.error.futureDateNotAllowed",
        ),
      );
      return;
    }
    if (endDateOnly.getTime() > todayDate.getTime()) {
      this.toastrService.error(
        this.translateService.instant(
          "dimbo.i18n.submissionHistory.error.futureDateNotAllowed",
        ),
      );
      return;
    }
    // End: CAP-39922 DMBR-64

    if (normalizedStart && normalizedEnd) {
      const diffDays = Math.round(
        (endDateOnly.getTime() - startDateOnly.getTime()) / (1000 * 60 * 60 * 24),
      );
      if (diffDays < 0) {
        // Start: CAP-39922 DMBR-64
        this.toastrService.error(
          this.translateService.instant(
            "dimbo.i18n.submissionHistory.error.endDateBeforeStart",
          ),
        );
        // End: CAP-39922 DMBR-64
        return;
      }
      if (diffDays > 366) {
        // Start: CAP-39922 DMBR-64
        this.toastrService.error(
          this.translateService.instant(
            "dimbo.i18n.submissionHistory.error.dateRangeExceeded",
          ),
        );
        // End: CAP-39922 DMBR-64
        return;
      }
    }

    const repairerId = this.searchForm.get("repairerId")?.value?.trim();

    // Start: CAP-39922 DMBR-64 — Validate RRDI / Physical Site format
    if (repairerId) {
      const repairerIdPattern = /^[A-Za-z0-9]+([\-_][A-Za-z0-9]+)*$/;
      if (!repairerIdPattern.test(repairerId)) {
        this.toastrService.error(
          this.translateService.instant(
            "dimbo.i18n.submissionHistory.error.repairerIdInvalidFormat",
          ),
        );
        return;
      }
      const alphanumericCount = repairerId.replace(/[-_]/g, "").length;
      if (alphanumericCount > 15) {
        this.toastrService.error(
          this.translateService.instant(
            "dimbo.i18n.submissionHistory.error.repairerIdMaxLength",
          ),
        );
        return;
      }
    }
    // End: CAP-39922 DMBR-64

    this.isVinSearch = false;
    this.hasSearched = true;
    this.resetResults();
    this.fetchFilterPage(0);
  }

  onVinSearch(): void {
    const vehicleVin = this.searchForm.get("vehicleVin")?.value?.trim();

    if (!vehicleVin) {
      // Start: CAP-39922 DMBR-64
      this.toastrService.warning(
        this.translateService.instant(
          "dimbo.i18n.submissionHistory.error.vinRequired",
        ),
      );
      // End: CAP-39922 DMBR-64
      return;
    }

    if (this.searchForm.get("vehicleVin")?.invalid) {
      this.searchForm.get("vehicleVin")?.markAsDirty();
      // Start: CAP-39922 DMBR-64
      if (this.searchForm.get("vehicleVin")?.hasError("pattern")) {
        this.toastrService.error(
          this.translateService.instant(
            "dimbo.i18n.vinSearchBox.error.invalidFormat",
          ),
        );
      } else if (this.searchForm.get("vehicleVin")?.hasError("minlength")) {
        this.toastrService.error(
          this.translateService.instant(
            "dimbo.i18n.vinSearchBox.error.invalidLength",
          ),
        );
      }
      // End: CAP-39922 DMBR-64
      return;
    }

    this.vinSearchPerformed.emit(vehicleVin);
    this.isVinSearch = true;
    this.hasSearched = true;
    this.resetResults();
    this.fetchVinResults(vehicleVin);
  }

  onSearch(): void {
    this.onFilterSearch();
  }

  /**
   * VIN search: fetch all results at once, then display progressively (50 at a time).
   */
  private fetchVinResults(vin: string): void {
    this.loading = true;
    this.vinSearchService.searchByVin(vin).subscribe({
      next: (results: SubmissionHistory[]) => {
        this.loading = false;
        this.allFetched = results || [];
        this.hasMoreOnServer = false; // VIN search returns all at once
        if (this.allFetched.length === 0) {
          this.errorMessage =
            "dimbo.i18n.api.error.noDataFound.submissionHistory";
        }
        this.isPanelExpanded = false;
        this.showNextChunk();
        this.vinSearchCompleted.emit({
          vin,
          results: this.allFetched,
          hasError: false,
        });
      },
      error: (err: any) => {
        this.loading = false;
        if (err?.status === 404) {
          this.errorMessage =
            "dimbo.i18n.api.error.noDataFound.submissionHistory";
        } else {
          this.errorMessage = "dimbo.i18n.brandcareForm.error.generic";
          // Start: CAP-39922 DMBR-64
          this.toastrService.error(
            this.translateService.instant(
              "dimbo.i18n.submissionHistory.error.searchFailed",
            ),
          );
          // End: CAP-39922 DMBR-64
        }
        this.vinSearchCompleted.emit({ vin, results: [], hasError: true });
      },
    });
  }

  /**
   * Filter search (RRDI / Physical Site): fetch one page at a time from the backend.
   */
  private fetchFilterPage(page: number): void {
    page === 0 ? (this.loading = true) : (this.loadingMore = true);

    const isPhysicalSite = this.searchForm.get("searchScope")?.value;
    const repairerId = this.searchForm.get("repairerId")?.value?.trim();
    const startDate = this.formatDate(this.searchForm.get("startDate")?.value);
    const endDate = this.formatDate(
      this.searchForm.get("endDate")?.value ?? this.maxDate,
    );

    const params: any = {
      startDate,
      endDate,
      page,
      size: this.PAGE_SIZE,
    };
    // Start: CAP-39922 DMBR-64 — Strip hyphens and underscores before sending to API
    if (repairerId) {
      const sanitizedRepairerId = repairerId.replace(/[-_]/g, "");
      if (isPhysicalSite) {
        params.physicalSite = sanitizedRepairerId;
      } else {
        params.userRrdi = sanitizedRepairerId;
      }
    }
    // End: CAP-39922 DMBR-64

    this.vinSearchService.searchByFilter(params).subscribe({
      // Start: CAP-39922 DMBR-64
      next: (response: SubmissionHistoryFilterResponse) => {
        this.loading = false;
        this.loadingMore = false;
        const batch = response.results || [];
        this.allFetched = this.allFetched.concat(batch);
        this.hasMoreOnServer = batch.length >= this.PAGE_SIZE;

        if (page === 0) {
          this.totalResultCount = response.totalCount || 0;
          this.filterStartDate = this.formatDisplayDate(
            this.searchForm.get("startDate")?.value,
          );
          this.filterEndDate = this.formatDisplayDate(
            this.searchForm.get("endDate")?.value ?? this.maxDate,
          );
        }

        if (page === 0 && this.allFetched.length === 0) {
          this.errorMessage =
            "dimbo.i18n.api.error.noDataFound.submissionHistory";
        }
        if (page === 0) {
          this.isPanelExpanded = false;
        }
        this.showNextChunk();
      },
      // End: CAP-39922 DMBR-64
      error: (err: any) => {
        this.loading = false;
        this.loadingMore = false;
        if (err?.status === 404) {
          if (page === 0) {
            this.errorMessage =
              "dimbo.i18n.api.error.noDataFound.submissionHistory";
          }
        } else {
          this.errorMessage = "dimbo.i18n.brandcareForm.error.generic";
          // Start: CAP-39922 DMBR-64
          this.toastrService.error(
            this.translateService.instant(
              "dimbo.i18n.submissionHistory.error.searchFailed",
            ),
          );
          // End: CAP-39922 DMBR-64
        }
      },
    });
  }

  private showNextChunk(): void {
    const slice = this.allFetched.slice(
      this.displayIndex,
      this.displayIndex + this.PAGE_SIZE,
    );
    this.displayedResults = this.displayedResults.concat(slice);
    this.displayIndex += slice.length;
  }

  private onScrollReached(): void {
    if (this.loading || this.loadingMore) return;

    const hasMoreInBuffer = this.displayIndex < this.allFetched.length;
    if (hasMoreInBuffer) {
      // More data already fetched, just display the next chunk
      this.showNextChunk();
    } else if (this.hasMoreOnServer && !this.isVinSearch) {
      // Filter search: fetch next page from backend
      this.fetchPage++;
      this.fetchFilterPage(this.fetchPage);
    }
  }

  private setupObserver(): void {
    if (!this.scrollAnchor?.nativeElement) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.onScrollReached();
        }
      },
      { root: null, rootMargin: "100px", threshold: 0 },
    );

    this.observer.observe(this.scrollAnchor.nativeElement);
  }

  private formatDate(date: Date): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // Start: CAP-39922 DMBR-64
  private formatDisplayDate(date: Date): string {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }
  // End: CAP-39922 DMBR-64

  onVehicleInfoChange(_event: any): void {
    // Vehicle info panel not used in new submission history mode
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}
